import { adminService } from '@/api/services';
import type { UnwrapData } from '@/api/types';

import type {
  ISpeakersResponse,
  ICreateSpeakerRequest,
  ICreateSpeakerResponse,
  IDeleteSpeakerRequest,
  IReorderSpeakersRequest,
  IUpdateSpeakerRequest,
  TUpdateSpeakerResponse,
} from './types';

export const schoolSpeakersService = adminService.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Получить спикеров школы
     */
    getSchoolSpeakers: builder.query<UnwrapData<ISpeakersResponse>, { schoolId: number }>({
      query: ({ schoolId }) => ({
        method: 'GET',
        url: `/schools/${schoolId}/speakers`,
      }),
      providesTags: (_, __, { schoolId }) => [{ type: 'SchoolSpeakers', id: schoolId }],
      transformResponse: (response: ISpeakersResponse): UnwrapData<ISpeakersResponse> => response.data,
    }),
    /**
     * Создать спикера школы
     */
    createSchoolSpeaker: builder.mutation<ICreateSpeakerResponse, ICreateSpeakerRequest>({
      query: ({ data, schoolId }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/speakers`,
        body: {
          data,
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolSpeakers', id: schoolId }],
    }),
    /**
     * Изменить спикера школы
     */
    updateSchoolSpeaker: builder.mutation<TUpdateSpeakerResponse, IUpdateSpeakerRequest>({
      query: ({ data, schoolId, speakerId }) => ({
        method: 'PATCH',
        url: `/schools/${schoolId}/speakers/${speakerId}`,
        body: {
          data,
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolSpeakers', id: schoolId }],
    }),
    /**
     * Удалить спикера школы
     */
    deleteSchoolSpeaker: builder.mutation<void, IDeleteSpeakerRequest>({
      query: ({ schoolId, speakerId }) => ({
        method: 'DELETE',
        url: `/schools/${schoolId}/speakers/${speakerId}`,
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolSpeakers', id: schoolId }],
    }),
    /**
     * Изменить порядок спикеров школы
     */
    reorderSchoolSpeakers: builder.mutation<ISpeakersResponse, IReorderSpeakersRequest>({
      query: ({ schoolId, data }) => ({
        method: 'PATCH',
        url: `/schools/${schoolId}/speakers/reorder`,
        body: {
          data,
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolSpeakers', id: schoolId }],
    }),
  }),
});

export const {
  useGetSchoolSpeakersQuery,
  useCreateSchoolSpeakerMutation,
  useUpdateSchoolSpeakerMutation,
  useDeleteSchoolSpeakerMutation,
  useReorderSchoolSpeakersMutation,
} = schoolSpeakersService;
